import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box, TextField, Typography, Checkbox, FormControlLabel,
  MenuItem, Divider,
} from '@mui/material';
import { styled } from '@mui/system';
import RTL from '../utils/RTL';
import StyledButton from '../components/CustomButton';
import api from '../config/axiosConfig';
import { toEnglishDigits } from '../utils/convertNumbers';
import logo from '../assets/logo.png';

const KIND_OPTIONS = [
  { value: 'music', label: 'موسیقی' },
  { value: 'art', label: 'نقاشی و هنرهای تجسمی' },
  { value: 'calligraphy', label: 'خوشنویسی' },
  { value: 'language', label: 'زبان' },
  { value: 'tutoring', label: 'آموزشی و کنکور' },
  { value: 'other', label: 'سایر' },
];

const STUDENT_OPTIONS = [
  { value: 1, label: 'کمتر از ۱۰۰ نفر' },
  { value: 2, label: 'بین ۱۰۰ تا ۲۰۰ نفر' },
  { value: 3, label: 'بین ۲۰۰ تا ۵۰۰ نفر' },
  { value: 4, label: 'بیش از ۵۰۰ نفر' },
];

// Fields whose value must reach the API as English digits
const NUMERIC_FIELDS = [
  'mobile_phone', 'landline_phone', 'national_id',
  'birth_date_day', 'birth_date_month', 'birth_date_year',
];

const REQUIRED_FIELDS = {
  first_name: 'نام',
  last_name: 'نام خانوادگی',
  institute_name: 'نام آموزشگاه',
  email: 'ایمیل',
  mobile_phone: 'تلفن همراه',
  province: 'استان',
  city: 'شهر',
};

const PageBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  minHeight: '100vh',
  padding: theme.spacing(4, 2),
  backgroundColor: theme.palette.background.neutral,
}));

const FormCard = styled(Box)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 2,
  padding: theme.spacing(4),
  backgroundColor: theme.palette.background.default,
  width: '100%',
  maxWidth: '44rem',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
}));

const FieldsGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(14rem, 1fr))',
  gap: theme.spacing(2),
  width: '100%',
}));

const StyledLogo = styled('img')(({ theme }) => ({
  width: '9rem',
  maxWidth: '50%',
  marginBottom: theme.spacing(1),
}));

const AccentLink = styled(Link)(({ theme }) => ({
  textDecoration: 'none',
  color: theme.palette.primary.main,
  fontWeight: 500,
  '&:hover': { textDecoration: 'underline' },
}));

const initialForm = {
  first_name: '', last_name: '', institute_name: '', email: '',
  mobile_phone: '', landline_phone: '', national_id: '',
  birth_date_day: '', birth_date_month: '', birth_date_year: '',
  province: '', city: '', address: '',
  kind_of_institute: 'music', number_of_students: 1,
  signed_contract: false,
};

export default function SignUp() {
  const navigate = useNavigate();
  const [form, setForm] = React.useState(initialForm);
  const [fieldErrors, setFieldErrors] = React.useState({});
  const [err, setErr] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [done, setDone] = React.useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((p) => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
    setFieldErrors((p) => ({ ...p, [name]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr(null);

    const missing = Object.entries(REQUIRED_FIELDS)
      .filter(([name]) => !String(form[name]).trim())
      .map(([, label]) => label);
    if (missing.length) {
      setErr(`لطفا این موارد را کامل کنید: ${missing.join('، ')}`);
      return;
    }
    if (!form.signed_contract) {
      setErr('برای ارسال درخواست باید قوانین استفاده را بپذیرید.');
      return;
    }

    const payload = { ...form };
    NUMERIC_FIELDS.forEach((name) => {
      payload[name] = toEnglishDigits(String(payload[name] || '')).trim();
    });

    try {
      setSubmitting(true);
      await api.post('/institutes/api/v1/signup-requests/', payload);
      setDone(true);
    } catch (error) {
      const data = error?.response?.data;
      if (error?.response?.status === 429) {
        setErr('تعداد درخواست‌ها بیش از حد مجاز است. لطفا کمی بعد دوباره تلاش کنید.');
      } else if (data && typeof data === 'object') {
        setFieldErrors(data);
        setErr('برخی از فیلدها نیاز به اصلاح دارند.');
      } else {
        setErr('ارسال درخواست ناموفق بود. لطفا دوباره تلاش کنید.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const fieldProps = (name) => ({
    name,
    value: form[name],
    onChange: handleChange,
    error: Boolean(fieldErrors[name]),
    helperText: Array.isArray(fieldErrors[name]) ? fieldErrors[name][0] : fieldErrors[name],
    fullWidth: true,
  });

  if (done) {
    return (
      <RTL>
        <PageBox sx={{ justifyContent: 'center' }}>
          <FormCard>
            <StyledLogo src={logo} alt="برنا" />
            <Typography variant="h5" sx={{ mb: 2, textAlign: 'center' }}>
              درخواست شما با موفقیت ثبت شد
            </Typography>
            <Typography sx={{ mb: 3, textAlign: 'center' }}>
              کارشناسان برنا پس از بررسی، برای ایجاد حساب کاربری آموزشگاه با شما تماس خواهند گرفت.
            </Typography>
            <StyledButton onClick={() => navigate('/')}>بازگشت به صفحه اصلی</StyledButton>
          </FormCard>
        </PageBox>
      </RTL>
    );
  }

  return (
    <RTL>
      <PageBox>
        <FormCard>
          <StyledLogo src={logo} alt="برنا" />
          <Typography variant="h5" sx={{ mb: 1, textAlign: 'center' }}>
            ایجاد حساب کاربری مدیر آموزشگاه
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
            مدیریت محترم، لطفا فرم زیر را کامل کنید. پس از بررسی درخواست، حساب آموزشگاه شما ایجاد
            می‌شود و اطلاعات ورود برایتان ارسال خواهد شد.
          </Typography>

          <form noValidate onSubmit={handleSubmit} style={{ width: '100%' }}>
            <FieldsGrid>
              <TextField label="نام" {...fieldProps('first_name')} />
              <TextField label="نام خانوادگی" {...fieldProps('last_name')} />
              <TextField label="نام آموزشگاه" {...fieldProps('institute_name')} />
              <TextField label="ایمیل" type="email" dir="ltr" {...fieldProps('email')} />
              <TextField label="تلفن همراه" {...fieldProps('mobile_phone')} />
              <TextField label="تلفن ثابت (اختیاری)" {...fieldProps('landline_phone')} />
              <TextField label="کد ملی (اختیاری)" {...fieldProps('national_id')} />
              <TextField label="استان" {...fieldProps('province')} />
              <TextField label="شهر" {...fieldProps('city')} />
            </FieldsGrid>

            <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
              تاریخ تولد (اختیاری):
            </Typography>
            <FieldsGrid sx={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
              <TextField label="روز" {...fieldProps('birth_date_day')} />
              <TextField label="ماه" {...fieldProps('birth_date_month')} />
              <TextField label="سال" {...fieldProps('birth_date_year')} />
            </FieldsGrid>

            <Box sx={{ mt: 2 }}>
              <TextField label="نشانی (اختیاری)" multiline rows={2} {...fieldProps('address')} />
            </Box>

            <FieldsGrid sx={{ mt: 2 }}>
              <TextField select label="نوع آموزشگاه" {...fieldProps('kind_of_institute')}>
                {KIND_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </TextField>
              <TextField select label="تعداد هنرجویان" {...fieldProps('number_of_students')}>
                {STUDENT_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </TextField>
            </FieldsGrid>

            <FormControlLabel
              sx={{ mt: 2 }}
              control={
                <Checkbox
                  name="signed_contract"
                  checked={form.signed_contract}
                  onChange={handleChange}
                />
              }
              label="قوانین استفاده از نرم‌افزار را مطالعه نموده‌ام و با آن‌ها موافق هستم"
            />

            {err && (
              <Typography color="error" sx={{ mt: 1, mb: 1 }}>{err}</Typography>
            )}

            <Box sx={{ mt: 2 }}>
              <StyledButton type="submit" disabled={submitting}>
                {submitting ? 'در حال ارسال…' : 'ارسال درخواست'}
              </StyledButton>
            </Box>
          </form>

          <Divider sx={{ width: '100%', mt: 3, mb: 2 }} />
          <Typography variant="body2">
            حساب کاربری دارید؟ <AccentLink to="/login">وارد شوید</AccentLink>
          </Typography>
        </FormCard>
      </PageBox>
    </RTL>
  );
}
